import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const css = `
  .auth-page { min-height:100vh; display:flex; align-items:center; justify-content:center; padding:24px; }
  .auth-card { background:var(--card); border:1px solid var(--border); border-radius:20px; padding:40px; max-width:420px; width:100%; }
  .auth-logo { font-family:var(--head); font-size:26px; font-weight:800; letter-spacing:2px; margin-bottom:24px; }
  .auth-logo span { color:var(--blue); }
  .auth-title { font-family:var(--head); font-size:22px; font-weight:800; letter-spacing:1px; margin-bottom:6px; }
  .auth-sub   { color:var(--muted); font-size:14px; margin-bottom:24px; line-height:1.5; }
  .auth-error { color:var(--danger); font-size:13px; margin-bottom:12px; text-align:center; }
  .auth-footer { text-align:center; margin-top:18px; color:var(--muted); font-size:14px; }
  .auth-footer a { color:var(--blue); font-weight:500; cursor:pointer; }
  .pw-strength { height:4px; border-radius:2px; margin-top:6px; transition:all .3s; }
`;

function getStrength(pw) {
  if (!pw) return { width: "0%", color: "transparent", label: "" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { width: "25%", color: "var(--danger)", label: "Weak" };
  if (score === 2) return { width: "50%", color: "#f59e0b", label: "Fair" };
  if (score === 3) return { width: "75%", color: "var(--teal)", label: "Good" };
  return { width: "100%", color: "#22c55e", label: "Strong" };
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [loading, setLoading]     = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState(null);
  const [ready, setReady]         = useState(false);

  // Supabase sends the user back with an access_token in the URL hash.
  // onAuthStateChange fires a PASSWORD_RECOVERY event when it detects it.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setReady(true);
      }
    });
    // Also check if session already exists (user already recovered)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match."); return; }
    if (password.length < 8)  { setError("Password must be at least 8 characters."); return; }
    setLoading(true); setError(null);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) { setError(error.message); setLoading(false); return; }
    setDone(true);
    setLoading(false);
    setTimeout(() => navigate("/dashboard"), 2500);
  };

  const strength = getStrength(password);

  if (!ready) {
    return (
      <>
        <style>{css}</style>
        <div className="auth-page">
          <div className="auth-card fade-in" style={{ textAlign:"center" }}>
            <div className="auth-logo">NEO<span> DETAIL</span></div>
            <div style={{ color:"var(--muted)", fontSize:14, marginTop:8 }}>
              Verifying your reset link…
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="auth-page">
        <div className="auth-card fade-in">
          <div className="auth-logo">NEO<span> DETAIL</span></div>

          {done ? (
            <div style={{ textAlign:"center", padding:"10px 0 20px" }}>
              <div style={{ fontSize:52, marginBottom:16 }}>✅</div>
              <div className="auth-title">PASSWORD UPDATED</div>
              <div className="auth-sub">
                Your password has been changed. Redirecting to your dashboard…
              </div>
            </div>
          ) : (
            <>
              <div className="auth-title">SET NEW PASSWORD</div>
              <div className="auth-sub">Choose a strong password for your account.</div>
              <form onSubmit={handleSubmit}>
                <div className="form-group" style={{ marginBottom:20 }}>
                  <label>New Password</label>
                  <input
                    type="password"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                  {password && (
                    <>
                      <div className="pw-strength" style={{ width: strength.width, background: strength.color }} />
                      <div style={{ fontSize:11, color: strength.color, marginTop:3 }}>{strength.label}</div>
                    </>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom:20 }}>
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    placeholder="Repeat password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                  />
                </div>
                {error && <div className="auth-error">{error}</div>}
                <button className="btn-primary full" type="submit" disabled={loading}>
                  {loading ? <><span className="spinner" /> Updating…</> : "Update Password →"}
                </button>
              </form>
            </>
          )}

          <div className="auth-footer">
            <span onClick={() => navigate("/login")} style={{ cursor:"pointer" }}>← Back to Sign In</span>
          </div>
        </div>
      </div>
    </>
  );
}
